/**
 * PDP Add-on Product — size-matched upsell beside the buy box.
 *
 * Two jobs:
 *
 * 1. SIZE MATCHING. Whatever size the shopper picks on the mattress, the add-on
 *    switches to the same size. The mattress variant changes without a page
 *    load, so we re-match on the theme's `variant-change` pub/sub event (the
 *    same hook promotional-badges.js already uses).
 *
 * 2. ONE-CALL ADD TO CART. Ticking the card does NOT write to the cart. The
 *    selection is held until the shopper presses the main Add to Cart, then we
 *    intercept that submit and post the mattress and the add-on together in a
 *    single /cart/add.js call, so the cart drawer opens once with both lines.
 *
 * Why intercept on `document` in the capture phase: product-form.js binds its
 * submit handler on the form itself, in its constructor — earlier than this
 * file runs. A listener on the form would therefore fire *after* it. Capturing
 * on an ancestor runs before the target's own listeners, so stopPropagation()
 * there is what actually keeps the theme's single-item handler from firing.
 */
(function () {
  'use strict';

  // The snippet loads this script once per add-on block, so a page with two
  // add-ons would otherwise register the submit interceptor twice and post the
  // cart twice — stopPropagation stops the event reaching the form, but it does
  // not stop a second listener on `document` itself.
  if (window.__pdpAddonProductInit) return;
  window.__pdpAddonProductInit = true;

  var SELECTED_CLASS = 'apx--selected';

  /* ----------------------------------------------------------------- utils */

  /** Loose compare for option values: case and surrounding space are noise. */
  function norm(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  /**
   * Pull the centimetre dimensions out of an option value.
   * "Queen (152x190cm)" -> { w: 152, l: 190 }. Values with no dimensions
   * ("S/SS - Single/Super Single") return null and are simply never eligible
   * for dimension-based matching.
   */
  function dimsOf(value) {
    var m = String(value || '').match(/(\d+)\s*[x×]\s*(\d+)\s*cm/i);
    if (!m) return null;
    var w = parseInt(m[1], 10);
    var l = parseInt(m[2], 10);
    if (!isFinite(w) || !isFinite(l)) return null;
    return { w: w, l: l };
  }

  function parseJSON(text, fallback) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  /**
   * Merchant override for pairs whose option values do not line up.
   * One rule per line: `Mattress value = Add-on value`. Blank lines and
   * lines starting with # are ignored.
   */
  function parseSizeMap(text) {
    var map = {};
    String(text || '')
      .split('\n')
      .forEach(function (line) {
        var trimmed = line.trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var at = trimmed.indexOf('=');
        if (at === -1) return;
        var from = norm(trimmed.slice(0, at));
        var to = trimmed.slice(at + 1).trim();
        if (from && to) map[from] = to;
      });
    return map;
  }

  /* -------------------------------------------------------------- matching */

  /**
   * Decide which add-on variant goes with the mattress size the shopper picked.
   *
   * Order: merchant map, then exact value match, then the configured no-match
   * behaviour. Only purchasable variants are ever returned — offering a size
   * that cannot be bought is worse than offering nothing.
   *
   * Returns { variant, exact } or null when there is nothing to offer.
   */
  function resolveVariant(card, mainValue) {
    var variants = (card._variants || []).filter(function (v) {
      return v && v.available;
    });
    if (!variants.length) return null;

    var wanted = norm(mainValue);

    // 1. Merchant map wins — it exists precisely to override our guesswork.
    var mapped = card._sizeMap[wanted];
    if (mapped) {
      var byMap = variants.filter(function (v) {
        return norm(v.value) === norm(mapped);
      })[0];
      if (byMap) return { variant: byMap, exact: true };
    }

    // 2. Exact value match — the happy path. Mattress and protector share
    //    byte-identical size strings, so this is what normally runs.
    var exact = variants.filter(function (v) {
      return norm(v.value) === wanted;
    })[0];
    if (exact) return { variant: exact, exact: true };

    // 3. No exact hit. Either the add-on does not stock this size at all, or
    //    the matching size is sold out.
    if (card._noMatch === 'hide') return null;
    if (card._noMatch === 'disable') return { variant: null, exact: false };

    return nearestVariant(variants, mainValue);
  }

  /**
   * Nearest-size fallback.
   *
   * Prefer the SMALLEST add-on whose width and length both meet or exceed the
   * mattress, because an oversized protector still fits a smaller mattress
   * while an undersized one does not. Only if nothing is big enough do we fall
   * back to the largest available. The chosen size stays visible on the card,
   * so the shopper always sees what they are actually getting.
   */
  function nearestVariant(variants, mainValue) {
    var want = dimsOf(mainValue);
    var sized = variants
      .map(function (v) {
        return { variant: v, dims: dimsOf(v.value) };
      })
      .filter(function (entry) {
        return entry.dims;
      });

    if (!want || !sized.length) {
      // Nothing measurable to compare — offering an arbitrary size would be a
      // guess, so decline rather than risk the wrong product.
      return null;
    }

    var area = function (d) {
      return d.w * d.l;
    };

    var fits = sized.filter(function (entry) {
      return entry.dims.w >= want.w && entry.dims.l >= want.l;
    });

    var pick;
    if (fits.length) {
      pick = fits.reduce(function (best, entry) {
        return area(entry.dims) < area(best.dims) ? entry : best;
      });
    } else {
      pick = sized.reduce(function (best, entry) {
        return area(entry.dims) > area(best.dims) ? entry : best;
      });
    }

    return { variant: pick.variant, exact: false };
  }

  /* ---------------------------------------------------------------- render */

  function setSelected(card, selected) {
    var button = card.querySelector('[data-addon-toggle]');
    var label = card.querySelector('[data-addon-toggle-label]');
    if (!button) return;

    card._selected = !!selected && !!card._match;
    button.setAttribute('aria-pressed', card._selected ? 'true' : 'false');
    card.classList.toggle(SELECTED_CLASS, card._selected);
    if (label) {
      label.textContent = card._selected ? card.dataset.addonAddedLabel : card.dataset.addonAddLabel;
    }
  }

  function note(card, message) {
    var el = card.querySelector('[data-addon-note]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /** Repaint the card for the mattress size currently selected. */
  function render(card, mainValue) {
    var result = resolveVariant(card, mainValue);
    var button = card.querySelector('[data-addon-toggle]');
    var sizeLabel = card.querySelector('[data-addon-size-label]');
    var priceEl = card.querySelector('[data-addon-price]');
    var compareEl = card.querySelector('[data-addon-compare]');

    // Nothing offerable for this size — take the card away entirely rather
    // than advertise something the shopper cannot buy.
    if (!result || (!result.variant && card._noMatch !== 'disable')) {
      card._match = null;
      setSelected(card, false);
      card.hidden = true;
      return;
    }

    card.hidden = false;

    if (!result.variant) {
      card._match = null;
      setSelected(card, false);
      if (button) button.disabled = true;
      if (sizeLabel) sizeLabel.textContent = '—';
      if (priceEl) priceEl.textContent = '';
      if (compareEl) compareEl.hidden = true;
      note(card, 'Not available for the selected size.');
      return;
    }

    var variant = result.variant;
    card._match = variant;
    if (button) button.disabled = false;

    if (sizeLabel) sizeLabel.textContent = variant.value;
    if (priceEl) priceEl.textContent = variant.price_formatted;

    if (compareEl) {
      var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEl.textContent = hasCompare ? variant.compare_formatted : '';
      compareEl.hidden = !hasCompare;
    }

    // Be explicit when the shopper is being offered a different size to the one
    // they picked, so a substitution is never silent.
    note(card, result.exact ? '' : 'Closest available size shown.');

    // A size change can invalidate a previous tick (different variant, possibly
    // different price) — keep the selection but re-point it at the new variant.
    setSelected(card, card._selected);
  }

  /* ------------------------------------------------------- variant reading */

  /** The mattress option value this card matches on, for a given variant. */
  function valueFromVariant(card, variant) {
    if (!variant || !variant.options) return null;
    var index = card._mainOptions.indexOf(card.dataset.addonOptionName);
    if (index === -1) index = 0;
    return variant.options[index];
  }

  /* ------------------------------------------------------------ cart write */

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /** Line-item properties. Underscore-prefixed: visible to Functions, hidden from the shopper. */
  function addonProperties(mainVariantId, qtyPer) {
    return {
      _addon_parent_variant_id: String(mainVariantId || ''),
      _addon_per_main_qty: String(qtyPer),
    };
  }

  function postCart(body) {
    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok, json: json };
      });
    });
  }

  function handleSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var productForm = form.closest('product-form');
    if (!productForm) return;

    // Only the cards belonging to THIS form's section, and only ticked ones
    // that resolved to a buyable variant. Scoping by section matters because a
    // quick-add modal can put a second product form on the same page — without
    // it, this product's add-on would ride along with someone else's submit.
    //
    // Read the section from <product-form data-section-id>, NOT from `form.id`.
    // A form exposes its named controls as properties, and this form contains
    // <input name="id"> for the variant — so `form.id` returns that input
    // element, not the id string. Calling .replace() on it throws, and a
    // listener that throws does not stop propagation, so the theme's own
    // handler would still run and add the mattress without the add-on.
    var sectionId = productForm.dataset.sectionId || form.getAttribute('id') || '';
    var cards = Array.prototype.filter.call(document.querySelectorAll('[data-addon-card]'), function (card) {
      if (sectionId && card.dataset.addonSection !== sectionId) return false;
      return card._selected && card._match && !card.hidden;
    });
    if (!cards.length) return; // nothing of ours — let the theme handle it

    var formData = new FormData(form);
    var mainId = formData.get('id');
    if (!mainId) return;

    var mainQty = parseInt(formData.get('quantity'), 10);
    if (!isFinite(mainQty) || mainQty < 1) mainQty = 1;

    // Carry over any properties the theme's own form already collects
    // (engraving, gift notes and the like) so intercepting loses nothing.
    var mainProperties = {};
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match) mainProperties[match[1]] = value;
    });

    event.preventDefault();
    event.stopPropagation();

    var submitButton = productForm.querySelector('[type="submit"]');
    var spinner = productForm.querySelector('.loading__spinner');
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    var items = [{ id: Number(mainId), quantity: mainQty, properties: mainProperties }];
    cards.forEach(function (card) {
      var per = parseInt(card.dataset.addonQtyPer, 10);
      if (!isFinite(per) || per < 1) per = 1;
      items.push({
        id: card._match.id,
        quantity: mainQty * per,
        properties: addonProperties(mainId, per),
      });
    });

    var cart = cartElement();
    var payload = { items: items };
    if (cart) {
      payload.sections = cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
      payload.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    postCart(payload)
      .then(function (result) {
        if (result.ok) return result;

        // /cart/add.js is all-or-nothing: if the add-on failed (sold out in the
        // seconds since render, say) the mattress was NOT added either. Retry
        // with the mattress alone so the shopper still gets what they came for,
        // and say what happened rather than failing silently.
        cards.forEach(function (card) {
          note(card, 'This add-on could not be added and was left out of your cart.');
          setSelected(card, false);
        });
        var fallback = { items: [items[0]] };
        if (payload.sections) {
          fallback.sections = payload.sections;
          fallback.sections_url = payload.sections_url;
        }
        return postCart(fallback);
      })
      .then(function (result) {
        var json = result.json;
        if (!result.ok) throw new Error(json && json.description ? json.description : 'Add to cart failed');

        if (cart && json.sections) {
          // A multi-item response carries `items` but none of the top-level
          // `key`/`id` that renderContents reads. Borrow them from the mattress
          // so the notification renders the main product, not undefined.
          if (!json.key && json.items && json.items.length) {
            json.key = json.items[0].key;
            json.id = json.items[0].id;
          }
          cart.renderContents(json);
        } else {
          window.location = window.routes.cart_url;
          return;
        }

        // Clear the tick so a second Add to Cart does not silently re-add it.
        cards.forEach(function (card) {
          if (card._selected) setSelected(card, false);
        });
      })
      .catch(function (error) {
        console.warn('[addon] add to cart failed:', error);
        cards.forEach(function (card) {
          note(card, 'Something went wrong adding this. Please try again.');
        });
      })
      .finally(function () {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  }

  /* --------------------------------------------------------------- startup */

  function initCard(card) {
    if (card._addonReady) return;
    card._addonReady = true;

    var variantsEl = card.querySelector('[data-addon-variants]');
    var mapEl = card.querySelector('[data-addon-size-map]');

    card._variants = variantsEl ? parseJSON(variantsEl.textContent, []) : [];
    card._sizeMap = mapEl ? parseSizeMap(mapEl.textContent) : {};
    card._mainOptions = parseJSON(card.dataset.addonMainOptions, []);
    card._noMatch = card.dataset.addonNoMatch || 'nearest';
    card._selected = false;
    card._match = null;

    var button = card.querySelector('[data-addon-toggle]');
    if (button) {
      button.addEventListener('click', function () {
        setSelected(card, !card._selected);
      });
    }

    var initial = parseJSON(card.dataset.addonMainInitial, []);
    var index = card._mainOptions.indexOf(card.dataset.addonOptionName);
    if (index === -1) index = 0;
    render(card, initial[index]);
  }

  function initAll() {
    document.querySelectorAll('[data-addon-card]').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-match whenever the mattress variant changes. The theme republishes this
  // on every picker interaction; product-info re-renders markup, so re-init any
  // card that came back as a fresh element.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data ? event.data.variant : null;
      if (!variant) return;
      document.querySelectorAll('[data-addon-card]').forEach(function (card) {
        initCard(card);
        render(card, valueFromVariant(card, variant));
      });
    });
  }

  document.addEventListener('submit', handleSubmit, true);
})();
